import "../styles/index.css"

const GRAPHQL_URL = "https://backend.uniguideme.com/graphql"

const CONSULTANTS_QUERY = `
  query HomepageConsultants($limit: Int!) {
    iecsPublic(limit: $limit, sortBy: RELEVANCE) {
      collection {
        id
        iecProfile {
          fullName
          location
          yearsOfExperience
          shortBio
          profilePicture
          serviceOfferings { name }
        }
      }
    }
  }
`

const PAGE_SIZE = 2
const VISIBLE_SERVICES = 2

/* ---------------------------------------------------------------- mobile nav */

function initMobileNav() {
  const menu = document.querySelector("[data-mobile-nav]")
  const openBtn = document.querySelector("[data-mobile-nav-toggle]")
  const closeBtn = document.querySelector("[data-mobile-nav-close]")
  if (!menu || !openBtn) return

  const setOpen = (open) => {
    menu.classList.toggle("hidden", !open)
    openBtn.setAttribute("aria-expanded", String(open))
    document.body.style.overflow = open ? "hidden" : ""
  }

  openBtn.addEventListener("click", () => setOpen(true))
  closeBtn?.addEventListener("click", () => setOpen(false))

  // Tapping any link inside the menu should dismiss it, including in-page anchors.
  menu.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => setOpen(false))
  })

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !menu.classList.contains("hidden")) setOpen(false)
  })
}

/* -------------------------------------------------------------- scroll reveal */

function initScrollReveal() {
  const targets = document.querySelectorAll("[data-reveal]")
  if (!targets.length) return

  // No IntersectionObserver (or reduced motion): show everything immediately
  // rather than leaving content stuck at opacity 0.
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (reduced || !("IntersectionObserver" in window)) {
    targets.forEach((el) => el.classList.add("is-revealed"))
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add("is-revealed")
        observer.unobserve(entry.target)
      })
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
  )

  targets.forEach((el) => observer.observe(el))
}

/* ------------------------------------------------------------------- FAQ */

function initAccordions() {
  document.querySelectorAll("[data-accordion-group]").forEach((group) => {
    const items = group.querySelectorAll("details[data-accordion]")
    items.forEach((item) => {
      item.addEventListener("toggle", () => {
        if (!item.open) return
        // One open at a time, matching the legacy behaviour.
        items.forEach((other) => {
          if (other !== item) other.open = false
        })
      })
    })
  })
}

/* ----------------------------------------------------- consultant carousel */

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("")
}

function escapeHtml(value) {
  const el = document.createElement("div")
  el.textContent = value ?? ""
  return el.innerHTML
}

function consultantCard(profile) {
  const name = profile.fullName || "Educational Consultant"
  const services = profile.serviceOfferings || []
  const shown = services.slice(0, VISIBLE_SERVICES)
  const remaining = services.length - shown.length

  const meta = [profile.location, profile.yearsOfExperience ? `${profile.yearsOfExperience}+ years experience` : null]
    .filter(Boolean)
    .join(" • ")

  // Initials placeholder is always rendered underneath; a successful avatar
  // covers it, and `onerror` removes a broken one so the initials show through
  // rather than leaving an empty circle.
  const fallback = `<span class="absolute inset-0 flex items-center justify-center rounded-full bg-[#e8f1ff] font-display t-body font-semibold text-brand-navy">${escapeHtml(initials(name))}</span>`

  const photo = profile.profilePicture?.url
    ? `<img src="${escapeHtml(profile.profilePicture.url)}" alt="" width="48" height="48" loading="lazy"
           class="absolute inset-0 h-12 w-12 rounded-full object-cover"
           onerror="this.remove()" />`
    : ""

  const avatar = `<span class="relative block h-12 w-12 shrink-0">${fallback}${photo}</span>`

  const pills = shown
    .map(
      (service) =>
        `<span class="rounded-full bg-[#eef4ff] px-3 py-1.5 t-btn fw-bold text-brand-blue">${escapeHtml(service.name)}</span>`
    )
    .join("")

  const morePill = remaining > 0
    ? `<span class="rounded-full bg-surface-alt px-3 py-1.5 t-btn fw-bold text-muted">+ ${remaining} more</span>`
    : ""

  return `
    <article class="flex flex-col rounded-[16px] border border-hairline p-6">
      <div class="flex items-center gap-4">
        ${avatar}
        <div class="min-w-0">
          <h3 class="t-card-title font-display text-ink">${escapeHtml(name)}</h3>
          <p class="mt-1 t-btn text-muted">${escapeHtml(meta)}</p>
        </div>
      </div>
      <p class="mt-5 line-clamp-3 t-body text-muted">${escapeHtml(profile.shortBio)}</p>
      <div class="mt-auto flex flex-wrap gap-2 pt-6">${pills}${morePill}</div>
    </article>
  `
}

async function initConsultantCarousel() {
  const section = document.querySelector("[data-consultants-section]")
  if (!section) return

  const track = section.querySelector("[data-consultant-track]")
  const controls = section.querySelector("[data-carousel-controls]")
  const dotsEl = section.querySelector("[data-carousel-dots]")
  const prevBtn = section.querySelector("[data-carousel-prev]")
  const nextBtn = section.querySelector("[data-carousel-next]")

  let profiles = []
  try {
    const response = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: CONSULTANTS_QUERY, variables: { limit: 8 } }),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const payload = await response.json()
    if (payload.errors?.length) throw new Error(payload.errors[0].message)

    profiles = (payload.data?.iecsPublic?.collection || [])
      .map((iec) => iec.iecProfile)
      .filter((profile) => profile && profile.fullName)
      // Alphabetical by name. localeCompare so accented names sort sensibly
      // rather than landing after Z the way a raw `<` comparison would.
      .sort((a, b) => a.fullName.localeCompare(b.fullName, "en", { sensitivity: "base" }))
  } catch (error) {
    // The marketing page must never look broken because the API is down.
    console.warn("[uniguideme] consultant carousel unavailable:", error)
    section.hidden = true
    return
  }

  if (!profiles.length) {
    section.hidden = true
    return
  }

  // One card per page below the tablet breakpoint, two above it. The query
  // matches --breakpoint-md in tailwind.css; keep the two in step.
  const wide = window.matchMedia("(min-width: 810px)")

  let pages = []
  let current = 0

  const paginate = () => {
    const size = wide.matches ? PAGE_SIZE : 1
    // Anchor on the first profile currently on screen so that crossing the
    // breakpoint keeps you roughly where you were instead of jumping to 0.
    const anchor = pages.length ? profiles.indexOf(pages[current][0]) : 0
    pages = []
    for (let i = 0; i < profiles.length; i += size) {
      pages.push(profiles.slice(i, i + size))
    }
    current = Math.min(Math.floor(Math.max(anchor, 0) / size), pages.length - 1)
  }

  const renderDots = () => {
    dotsEl.innerHTML = pages
      .map(
        (_, index) =>
          `<span class="h-1.5 rounded-full transition-all ${
            index === current ? "w-6 bg-brand-blue" : "w-1.5 bg-hairline"
          }"></span>`
      )
      .join("")
  }

  const paint = () => {
    track.innerHTML = pages[current].map(consultantCard).join("")
    renderDots()
    prevBtn.disabled = current === 0
    nextBtn.disabled = current === pages.length - 1
    controls.hidden = pages.length <= 1
  }

  // Slide the outgoing cards away in the direction of travel, swap the markup
  // while they're invisible, then slide the new ones in from the opposite
  // edge. `animating` guards against a fast double-click landing a swap
  // mid-transition and leaving the track stuck at opacity 0.
  const SLIDE_MS = 220
  let animating = false

  const go = (nextIndex, direction) => {
    if (animating || nextIndex === current || nextIndex < 0 || nextIndex >= pages.length) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduced) {
      current = nextIndex
      paint()
      return
    }

    animating = true
    const outClass = direction > 0 ? "is-exit-left" : "is-exit-right"
    const inClass = direction > 0 ? "is-enter-right" : "is-enter-left"

    track.classList.add(outClass)

    window.setTimeout(() => {
      current = nextIndex
      paint()
      track.classList.remove(outClass)
      track.classList.add(inClass)

      // Next frame, drop the enter class so the transition runs to rest.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          track.classList.remove(inClass)
          animating = false
        })
      })
    }, SLIDE_MS)
  }

  prevBtn.addEventListener("click", () => go(current - 1, -1))
  nextBtn.addEventListener("click", () => go(current + 1, 1))

  // Re-paginate when crossing the breakpoint, not on every resize tick.
  wide.addEventListener("change", () => {
    paginate()
    paint()
  })

  paginate()
  paint()
}

/* ---------------------------------------------------------------- contact */

function initContactForm() {
  const form = document.querySelector("[data-contact-form]")
  if (!form) return

  const successEl = form.querySelector("[data-form-success]")
  const errorEl = form.querySelector("[data-form-error]")
  const submitBtn = form.querySelector("button[type=submit]")

  form.addEventListener("submit", async (event) => {
    event.preventDefault()

    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    successEl.classList.add("hidden")
    errorEl.classList.add("hidden")
    submitBtn.disabled = true
    submitBtn.textContent = "Sending…"

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      form.reset()
      successEl.classList.remove("hidden")
    } catch (error) {
      console.warn("[uniguideme] contact submission failed:", error)
      errorEl.classList.remove("hidden")
    } finally {
      submitBtn.disabled = false
      submitBtn.textContent = "Submit"
    }
  })
}

/* -------------------------------------------------------------------- init */

function init() {
  initMobileNav()
  initScrollReveal()
  initAccordions()
  initContactForm()
  initConsultantCarousel()
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init)
} else {
  init()
}
